// The positions a FINAL game was actually played with, and the edits a coach
// makes to them afterwards. Pure: no Firebase, so scripts/test-game-positions.ts
// can cover it. The screen is src/components/GamePositionsEditor.tsx.
//
// A game's `inningLogs` ({ "1": [{ id, position }], "2": [...] }) is the only
// record of who played where. It feeds the Stats tab field time (when it is the
// game's record under fieldTimeRecordFor), Home field time, and each player's
// benchLog, which drives Field Time Fairness. Nothing here may generate a lineup:
// a final game's record is only ever edited cell by cell (support log G2 / P1).

import { fieldTimeRecordFor, gameLengthInnings, gcInningsToDecimal, type FieldTimeSource } from './gc-innings';

export type InningEntry = { id: string; position: string };

/**
 * Put `playerId` at `position` in one inning's lineup, swapping with whoever
 * held it. Pure, so it can be reused for both the manual swap and for seating a
 * newly chosen starter at P.
 *
 * Every player is present in the array with a position (BENCH and EH included),
 * which is what makes this a true swap rather than an insert. If the target slot
 * was empty, the incoming player's old slot is left vacant.
 */
export function swapIntoPosition(inning: any[], position: string, playerId: string): any[] {
  const incoming = inning.find(p => p.id === playerId);
  // A player the inning does not list yet (added late, or the inning was only
  // partially built) is appended rather than ignored. Returning `inning`
  // untouched here would make his position selector look stuck.
  if (!incoming) return [...inning, { id: playerId, position }];
  if (incoming.position === position) return inning;
  const vacated = incoming.position;
  // Every occupant currently at the target slot is bumped, not just the first
  // one found. Positions other than BENCH/EH should never hold more than one
  // player, but if a prior bug (or an inning carried over from a stale plan)
  // left two players sharing a slot, a plain single-swap would only bump one
  // of them and leave the duplicate standing. Bumping all of them collapses
  // the duplicate back to the one-player-per-slot invariant instead of
  // perpetuating it, while still behaving as an ordinary two-player swap in
  // the normal case.
  const displaced = position === 'BENCH' || position === 'EH'
    ? []
    : inning.filter(p => p.id !== playerId && p.position === position);
  return inning.map(p => {
    if (p.id === playerId) return { ...p, position };
    if (displaced.some(d => d.id === p.id)) return { ...p, position: vacated };
    return p;
  });
}

export type PositionsSource = 'logs' | 'plan' | 'blank';

export interface LoadedPositions {
  /** 0-based innings, each the list of players in that inning. */
  innings: InningEntry[][];
  numInnings: number;
  /** Who played, in display order (batting order when known). */
  rowIds: string[];
  /** Where the starting grid came from. 'plan' shows the "Starting from your plan" label. */
  source: PositionsSource;
}

/** Sort ids by the plan's batting order, then the live lineup's, then as given. */
function inGameOrder(ids: string[], game: any): string[] {
  const planOrder: string[] = game?.plan?.playerOrder ?? [];
  const lineupOrder: string[] = (game?.lineup ?? []).map((p: any) => p?.id).filter(Boolean);
  const rank = (id: string) => {
    const a = planOrder.indexOf(id);
    if (a !== -1) return a;
    const b = lineupOrder.indexOf(id);
    if (b !== -1) return 1000 + b;
    return 2000 + ids.indexOf(id);
  };
  return [...ids].sort((x, y) => rank(x) - rank(y));
}

const clone = (inning: any[]): InningEntry[] =>
  (inning || []).filter((e: any) => e?.id).map((e: any) => ({ id: e.id, position: e.position || 'BENCH' }));

/**
 * The editor's starting grid for a final game.
 *   1. The game's own logged positions, exactly as recorded.
 *   2. Else the pre-game plan (absent players left out), for the coach to correct.
 *   3. Else everyone available on the bench, for the coach to fill in.
 */
export function loadGamePositions(game: any, gameDayPlayerIds: string[], defaultInnings = 6): LoadedPositions {
  const logs = game?.inningLogs;
  if (logs && typeof logs === 'object' && Object.keys(logs).length > 0) {
    const keys = Object.keys(logs).map(Number).filter(k => Number.isFinite(k) && k >= 1);
    const numInnings = Math.max(...keys);
    const innings: InningEntry[][] = Array.from({ length: numInnings }, (_, i) => clone(logs[String(i + 1)]));
    const seen: string[] = [];
    innings.forEach(inn => inn.forEach(e => { if (!seen.includes(e.id)) seen.push(e.id); }));
    return { innings, numInnings, rowIds: inGameOrder(seen, game), source: 'logs' };
  }

  const planInnings = game?.plan?.innings;
  if (planInnings && typeof planInnings === 'object' && Object.keys(planInnings).length > 0) {
    const absent: string[] = game.plan.absentPlayerIds ?? [];
    const keys = Object.keys(planInnings).map(Number).filter(k => Number.isFinite(k) && k >= 0);
    const numInnings = game.plan.numInnings || Math.max(...keys) + 1;
    const innings: InningEntry[][] = Array.from({ length: numInnings }, (_, i) =>
      clone(planInnings[String(i)]).filter(e => !absent.includes(e.id)));
    const seen: string[] = [];
    innings.forEach(inn => inn.forEach(e => { if (!seen.includes(e.id)) seen.push(e.id); }));
    return { innings, numInnings, rowIds: inGameOrder(seen, game), source: 'plan' };
  }

  // As long as the game was (GameChanger's measure when imported), so filling
  // in every row makes a whole-game record. A walk-off's partial inning rounds up.
  const numInnings = Math.ceil(gameLengthInnings(game, defaultInnings) - 0.01);
  const rowIds = [...gameDayPlayerIds];
  return {
    innings: Array.from({ length: numInnings }, () => rowIds.map(id => ({ id, position: 'BENCH' }))),
    numInnings,
    rowIds,
    source: 'blank',
  };
}

/** Add a player who played to every inning, on the bench, for the coach to place. */
export function addPlayerToGame(innings: InningEntry[][], playerId: string): InningEntry[][] {
  return innings.map(inn => (inn.some(e => e.id === playerId) ? inn : [...inn, { id: playerId, position: 'BENCH' }]));
}

/** Take a player out of the game entirely (he was not there). */
export function removePlayerFromGame(innings: InningEntry[][], playerId: string): InningEntry[][] {
  return innings.map(inn => inn.filter(e => e.id !== playerId));
}

/**
 * The `inningLogs` map to save: keys "1".."N", one per inning, every inning
 * written (liveLoggedInnings counts keys, so a missing key reads as a short game).
 */
export function toInningLogs(innings: InningEntry[][], numInnings: number): Record<string, InningEntry[]> {
  const out: Record<string, InningEntry[]> = {};
  for (let i = 0; i < numInnings; i++) out[String(i + 1)] = clone(innings[i] ?? []);
  return out;
}

/**
 * The logs End Game saves. End Game used to keep only innings closed with End
 * Inning, so going straight from the last inning to End Game lost that inning's
 * positions (support log G10). Adds the inning in progress when the game was
 * being tracked inning by inning and that inning isn't logged yet. Between
 * innings it already is (End Inning logs it first), so this never adds a phantom
 * extra inning, and a game nobody tracked doesn't get a one-inning stub.
 * Returns `inningLogs` itself when there is nothing to add.
 */
export function logsWithInningInProgress<T extends Record<string | number, InningEntry[]>>(
  inningLogs: T,
  currentInning: number,
  lineup: { id: string; position: string }[],
): T {
  if (Object.keys(inningLogs || {}).length === 0 || inningLogs[currentInning]) return inningLogs;
  return { ...inningLogs, [currentInning]: lineup.map(p => ({ id: p.id, position: p.position })) };
}

/** Bench innings per player in one game, counted exactly as the live game's End Game does (BENCH and EH). */
export function benchInningsByPlayer(inningLogs: Record<string, InningEntry[]>): Record<string, number> {
  const out: Record<string, number> = {};
  Object.values(inningLogs).forEach(inn => (inn || []).forEach(e => {
    if (!(e.id in out)) out[e.id] = 0;
    if (e.position === 'BENCH' || e.position === 'EH') out[e.id]++;
  }));
  return out;
}

/**
 * Each player's updated bench record after this game's positions change.
 * Returns only players whose numbers actually move. A player no longer in the
 * game loses this game's entry rather than keeping a stale count.
 */
export function benchLogUpdates(
  players: any[],
  gameId: string,
  inningLogs: Record<string, InningEntry[]>,
): { id: string; benchLog: Record<string, number>; seasonBenchInnings: number }[] {
  const counts = benchInningsByPlayer(inningLogs);
  const updates: { id: string; benchLog: Record<string, number>; seasonBenchInnings: number }[] = [];
  for (const p of players) {
    const prior: Record<string, number> = p?.benchLog && typeof p.benchLog === 'object' ? p.benchLog : {};
    const inGame = p.id in counts;
    if (!inGame && !(gameId in prior)) continue;
    if (inGame && prior[gameId] === counts[p.id]) continue;
    const benchLog = { ...prior };
    if (inGame) benchLog[gameId] = counts[p.id];
    else delete benchLog[gameId];
    const seasonBenchInnings = Object.values(benchLog).reduce((s, n) => s + (Number(n) || 0), 0);
    updates.push({ id: p.id, benchLog, seasonBenchInnings });
  }
  return updates;
}

/**
 * Bench counts to rewrite when a team switches "Field positions come from"
 * (Settings → Field Time). Each player's benchLog drives Field Time Fairness,
 * and until now a GameChanger import overwrote it even when the coach's Dugout
 * live log was the accurate record (support log G9).
 *
 * Only games where the setting changes the answer are touched: a Dugout live
 * log covering the whole game AND GameChanger field time. For those, each
 * player's entry becomes what that source says (Dugout: BENCH/EH innings, as End
 * Game counts them; GameChanger: game length minus innings in the field, as the
 * import counts them). A player absent from the chosen record loses the game's
 * entry. Every other game's entry is left exactly as it is. Pass current-season
 * games.
 */
export function benchLogRebuildForSource(
  players: any[],
  seasonGames: any[],
  source: FieldTimeSource,
  defaultInnings = 6,
): { updates: { id: string; benchLog: Record<string, number>; seasonBenchInnings: number }[]; gamesAffected: number } {
  const perGame: { gameId: string; counts: Record<string, number> }[] = [];
  for (const g of seasonGames) {
    if (g?.status !== 'completed' || g?.isPractice || !g?.id) continue;
    if (fieldTimeRecordFor(g, 'dugout', defaultInnings) !== 'live') continue;
    if (fieldTimeRecordFor(g, 'gamechanger', defaultInnings) !== 'gc') continue;

    let counts: Record<string, number>;
    if (source === 'dugout') {
      counts = benchInningsByPlayer(g.inningLogs);
    } else {
      // The same game length the import used for its bench counts.
      const gameInnings = gameLengthInnings(g, defaultInnings);
      counts = {};
      for (const [pid, positions] of Object.entries(g.gcFieldTime || {})) {
        const inField = Object.values((positions as any) || {}).reduce<number>((sum, v) => sum + gcInningsToDecimal(v), 0);
        counts[pid] = Math.max(0, gameInnings - inField);
      }
    }
    perGame.push({ gameId: g.id, counts });
  }

  const updates: { id: string; benchLog: Record<string, number>; seasonBenchInnings: number }[] = [];
  for (const p of players) {
    const prior: Record<string, number> = p?.benchLog && typeof p.benchLog === 'object' ? p.benchLog : {};
    const benchLog = { ...prior };
    let changed = false;
    for (const { gameId, counts } of perGame) {
      if (p.id in counts) {
        if (benchLog[gameId] !== counts[p.id]) { benchLog[gameId] = counts[p.id]; changed = true; }
      } else if (gameId in benchLog) {
        delete benchLog[gameId];
        changed = true;
      }
    }
    if (!changed) continue;
    const seasonBenchInnings = Object.values(benchLog).reduce((sum, n) => sum + (Number(n) || 0), 0);
    updates.push({ id: p.id, benchLog, seasonBenchInnings });
  }
  return { updates, gamesAffected: perGame.length };
}
